
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { WindowCanvasConfig } from '@/interfaces/components/combinations/WindowCanvasConfig';

@Component({
    name: 'CombinationsConfiguratorCanvas',
    components: {},
})
export default class CombinationsConfiguratorCanvas extends Vue {
    @Prop({ required: true }) private windowConfig!: WindowCanvasConfig;
    @Prop() private index!: number;
    @Prop({ type: String, required: true }) private guideType!: string;
    @Prop({ type: String, required: true }) private secondGuideType!: string;
    @Prop({ type: Boolean, default: false }) private isFirstWindow!: boolean;
    @Prop({ type: Boolean, default: false }) private isLastWindow!: boolean;
    @Prop({ type: String, default: '' }) private combinationType!: string;
    @Prop({ type: Boolean, required: true }) private isLastStep!: boolean;
    @Prop({ type: Number, required: true }) private widthDifference!: number;
    @Prop({ type: Number, required: true }) private totalProductsSelected!: number;
    @Prop({ type: Number, required: true }) private currentStep!: number;
    @Prop({ type: Number, required: true }) private currentProductFormId!: number;

    // Hover state for guide images
    private hoveredGuide: string | null = null;
    private zoomFactor = 3.5; // How much larger the zoomed image should be compared to the original
    private zoomOffset = 70; // Distance to offset the zoomed image from the original

    // Props for the circle-letter element above the window
    @Prop({ type: Array, default: () => [] }) private circleLetters!: Array<{ letter: string; visible: boolean }>;

    // Rolette specific properties
    private slatHeight = 15;
    private railWidth = 20;
    private headerHeight = 40;
    private openSectionHeight = 100;
    private bottomLineHeight = 5; // Added new property for bottom line height
    private grayColor = '#cccccc'; // Grey color for header

    private topViewDescription: any = this.$t('Top-down View');
    private windowDescription: any = this.$t('View From the Inside Out');

    // Configuration for the description text rendered as a Konva text node
    private descriptionTextConfig(position: 'top' | 'middle' | 'bottom') {
        let description;

        switch (position) {
            case 'top':
                description = this.topViewDescription;
                break;
            case 'middle':
                description = this.windowDescription;
                break;
            case 'bottom':
                description = ''; // Default empty text for bottom position
                break;
            default:
                description = '';
        }

        const textWidth = this.calculateTextWidth(description);

        return {
            x: 20, // Center text horizontally within the background
            y: 20, // Center vertically (half of the 72px height)
            text: description,
            fontSize: 35,
            fontFamily: 'Helvetica, Arial, sans-serif',
            fontStyle: 'normal',
            fill: '#797979', // Match the template component color
            align: 'center',
            verticalAlign: 'middle',
            width: textWidth,
            perfectDrawEnabled: true,
            shadowForStrokeEnabled: false,
            wrap: 'none', // Prevent text wrapping
        };
    }

    // Configuration for the elegant background of the text
    private textBackgroundConfig(position: 'top' | 'middle' | 'bottom') {
        let description;

        switch (position) {
            case 'top':
                description = this.topViewDescription;
                break;
            case 'middle':
                description = this.windowDescription;
                break;
            case 'bottom':
                description = ''; // Default empty text for bottom position
                break;
            default:
                description = '';
        }

        const textWidth = this.calculateTextWidth(description);
        // Increase horizontal padding to give more space around the text
        const horizontalPadding = 40;

        return {
            x: 0, // Position background at the beginning of the x-axis
            y: 0,
            width: textWidth + horizontalPadding,
            height: 72, // Adjust height to match template
            fill: '#f5f5f5', // Match template background color
            stroke: '#e0e0e0', // Match template border color
            strokeWidth: 1,
            cornerRadius: 6, // Match template border radius
            shadowColor: 'rgba(0, 0, 0, 0.08)', // Match template shadow
            shadowBlur: 4,
            shadowOffset: { x: 0, y: 2 },
            shadowOpacity: 1,
            perfectDrawEnabled: true,
        };
    }

    // Calculate the ideal width for the text based on its content
    private calculateTextWidth(text: string): number {
        // Approximate width per character (in pixels) - increased for larger font size
        const pixelsPerChar = 20;

        // Calculate width based on text length with no minimum constraint
        const calculatedWidth = text.length * pixelsPerChar;

        // Set a reasonable maximum width for very long text
        const maxWidth = 800;

        return Math.min(calculatedWidth, maxWidth);
    }

    // Configuration for the position text rendered in the middle of the rectangle
    private get positionTextConfig() {
        const frameWidth = this.transformedDimensions?.width * this.groupScaleX;
        const position = this.windowConfig.position?.toString() || '';

        return {
            x: frameWidth / 2, // Center horizontally
            y: 30, // Center vertically in the 70px height rectangle
            // text: this.currentProductFormId + 1,
            text: position,
            fontSize: 24,
            fontFamily: 'Arial, Helvetica, sans-serif',
            fontStyle: 'bold',
            fontVariant: 'normal',
            fill: '#333333', // Dark gray text
            align: 'center',
            verticalAlign: 'middle',
            perfectDrawEnabled: false, // Disable for better text rendering
            shadowForStrokeEnabled: false,
            hitStrokeWidth: 0,
            listening: false, // Not interactive, improves performance
            transformsEnabled: 'position', // Only position transforms for better quality
            visible: true,
        };
    }

    private get isUsingExtraWidth() {
        return this.widthDifference > 0;
    }

    private get extraWidth() {
        if (this.isUsingExtraWidth) {
            // Increase width by a percentage of the difference to avoid too much stretching
            return Math.floor(this.widthDifference / 2);
        }

        return 0;
    }

    private get isStepThreeOrHigher() {
        return this.currentStep >= 3;
    }

    // Computed property to generate slats
    private get slats() {
        const frameHeight = this.transformedDimensions?.height * this.groupScaleY;
        const frameWidth = this.transformedDimensions?.width * this.groupScaleX;
        const slats = [];

        // Calculate how many slats we need based on frame height
        // Leaving space for the header and open section at bottom
        const availableHeight = frameHeight - this.headerHeight - this.openSectionHeight;
        const totalSlats = Math.floor(availableHeight / this.slatHeight);

        for (let i = 0; i < totalSlats; i++) {
            slats.push({
                y: this.headerHeight + i * this.slatHeight,
                width: frameWidth,
                height: this.slatHeight - 2, // Small gap between slats
            });
        }

        return slats;
    }

    private get groupX() {
        // Use positionX if available, otherwise fall back to initialPositionX
        return this.windowConfig.positionX !== null ? this.windowConfig.positionX : this.windowConfig.initialPositionX;
    }

    private get groupY() {
        // Use positionY if available, otherwise fall back to initialPositionY
        return this.windowConfig.positionY !== null ? this.windowConfig.positionY : this.windowConfig.initialPositionY;
    }

    private get groupScaleX() {
        // return (this.stageConfig.width / this.windowConfig.width) * 0.2;
        return 0.42;
    }

    private get groupScaleY() {
        // return (this.stageConfig.height / this.windowConfig.height) * 0.5;
        return 0.42;
    }

    private get transformedDimensions(): { height: number; width: number } {
        // Since we're only using CM, we directly return the dimensions
        return {
            height: parseFloat(this.windowConfig.height as any) * 10,
            width: parseFloat(this.windowConfig.width as any) * 10,
        };
    }

    private get isFirstGuideSingle() {
        return this.guideType === 'Enojno';
    }

    private get isSecondGuideSingle() {
        return this.secondGuideType === 'Enojno';
    }

    private get areBothGuidesSingle() {
        return this.isFirstGuideSingle && this.isSecondGuideSingle;
    }

    private get isFirstGuidePartition() {
        return this.guideType === 'Pregradno';
    }

    private get isSecondGuidePartition() {
        return this.secondGuideType === 'Pregradno';
    }

    private get areBothGuidesPartition() {
        return this.isFirstGuidePartition && this.isSecondGuidePartition;
    }

    private get windowPosition() {
        return this.windowConfig?.position ? parseInt(this.windowConfig.position.toString(), 10) : null;
    }

    private get shouldShowPartitionGuide() {
        if (!this.windowConfig?.position) return false;

        const isFirstGuidePartition = this.guideType === 'Pregradno';
        const isSecondGuidePartition = this.secondGuideType === 'Pregradno';

        return (
            (isFirstGuidePartition && this.windowPosition === 1) ||
            (isSecondGuidePartition && this.windowPosition === 2)
        );
    }

    // New method for rolette components configuration
    private get roletteConfigData() {
        // Create a computed property that depends on guideType
        // This will cache the result and only recalculate when guideType changes
        const blueFill = '#1a53ff'; // Blue color for rails
        const blueOutline = '#0033cc';

        return {
            leftRailColor: this.showLeftGuideImage && this.currentStep === 4 ? blueFill : this.grayColor,
            leftRailOutline: this.showLeftGuideImage && this.currentStep === 4 ? blueOutline : this.grayColor,
            rightRailColor: this.showRightGuideImage && this.currentStep === 4 ? blueFill : this.grayColor,
            rightRailOutline: this.showRightGuideImage && this.currentStep === 4 ? blueOutline : this.grayColor,
        };
    }

    private get showGuides() {
        return (!!this.guideType || !!this.secondGuideType) && this.isLastStep;
    }

    private roletteConfig(type: string) {
        const frameWidth = this.transformedDimensions?.width * this.groupScaleX;
        const frameHeight = this.transformedDimensions?.height * this.groupScaleY;

        // Use the computed property which will update when guideType changes
        const { leftRailColor, leftRailOutline, rightRailColor, rightRailOutline } = this.roletteConfigData;

        switch (type) {
            case 'header':
                return {
                    x: 0,
                    y: 0,
                    width: frameWidth,
                    height: this.headerHeight,
                    fill: '#cccccc', // Gray color for the header
                    stroke: '#999999',
                    strokeWidth: 1,
                };
            case 'leftRail':
                return {
                    x: 0,
                    y: 0,
                    width: this.railWidth,
                    height: frameHeight,
                    fill: leftRailColor, // Blue color for rail
                    stroke: leftRailOutline,
                    strokeWidth: 1,
                };
            case 'rightRail':
                return {
                    x: frameWidth - this.railWidth,
                    y: 0,
                    width: this.railWidth,
                    height: frameHeight,
                    fill: rightRailColor, // Blue color for rail
                    stroke: rightRailOutline,
                    strokeWidth: 1,
                };
            case 'windowOpening':
                return {
                    x: 0 + this.railWidth * 3.5,
                    y: frameHeight - this.openSectionHeight * 1.2,
                    width: frameWidth - this.railWidth * 7,
                    height: this.openSectionHeight * 0.9,
                    fill: 'white', // Window area
                    stroke: '#aaaaaa',
                    strokeWidth: 1,
                };
            case 'bottomLine':
                return {
                    x: 0,
                    y: frameHeight - this.bottomLineHeight,
                    width: frameWidth,
                    height: this.bottomLineHeight,
                    fill: '#666666', // Dark gray for bottom line
                    stroke: '#444444',
                    strokeWidth: 1,
                };
            default:
                return {};
        }
    }

    // Configuration for the dark background behind slats
    private get slatsBackgroundConfig() {
        const frameWidth = this.transformedDimensions?.width * this.groupScaleX;
        const frameHeight = this.transformedDimensions?.height * this.groupScaleY;
        const availableHeight = frameHeight - this.headerHeight - this.openSectionHeight - this.slatHeight;

        return {
            x: this.railWidth, // Start after left rail
            y: this.headerHeight, // Start after header
            width: frameWidth - this.railWidth * 2, // Width between rails
            height: availableHeight,
            fill: '#444444', // Dark gray background
            stroke: '#333333',
            strokeWidth: 0.5,
        };
    }

    // Configuration for individual slats
    private slatConfig(slat: any) {
        return {
            x: 0,
            y: slat.y,
            width: slat.width,
            height: slat.height,
            fill: '#e0e0e0', // Lighter gray for slats to contrast with dark background
            stroke: '#cccccc',
            strokeWidth: 0.5,
        };
    }

    // Shared configuration for colors that depends on guideType
    private get colorConfig() {
        const yellowColor = '#FFD700'; // Gold/yellow color
        const outlineColor = '#FFC107';

        return {
            lineColor: this.shouldShowPartitionGuide ? yellowColor : this.grayColor,
            strokeColor: this.shouldShowPartitionGuide ? outlineColor : this.grayColor,
        };
    }

    // Configuration for the rectangle above the window
    private get rectangleConfig() {
        const frameWidth = this.transformedDimensions?.width * this.groupScaleX;

        return {
            x: 0,
            y: 0,
            width: frameWidth,
            height: 70,
            fill: this.grayColor, // Light gray background
            stroke: '#333333',
            strokeWidth: 1,
            cornerRadius: 4,
            perfectDrawEnabled: true, // Improves rendering quality
            shadowForStrokeEnabled: false, // Better performance
            strokeScaleEnabled: false, // For smoother rendering of strokes
        };
    }

    private get showExtraWindowWidth() {
        return this.isUsingExtraWidth && this.isStepThreeOrHigher && this.totalProductsSelected >= 2;
    }

    // Configuration for the dashed rectangle showing extra width
    private get dashedExtraWidthConfig() {
        const frameWidth = this.transformedDimensions?.width * this.groupScaleX;
        const extraWidth = this.extraWidth * 10 * this.groupScaleX;

        // Configure based on whether it's first or last window
        if (this.isFirstWindow) {
            return {
                x: -extraWidth,
                y: 0,
                width: extraWidth,
                height: 70,
                stroke: '#1a53ff', // Blue stroke for dashed line
                strokeWidth: 1,
                dash: [3, 3], // Shorter dashes for better visual appearance
                fill: 'rgba(230, 240, 255, 1)', // Lighter blue tint background
                perfectDrawEnabled: true,
                shadowForStrokeEnabled: false,
                strokeScaleEnabled: false,
            };
        } else if (this.isLastWindow) {
            return {
                x: frameWidth,
                y: 0,
                width: extraWidth,
                height: 70,
                stroke: '#1a53ff', // Blue stroke for dashed line
                strokeWidth: 1,
                dash: [3, 3], // Shorter dashes for better visual appearance
                fill: 'rgba(230, 240, 255, 1)', // Lighter blue tint background
                perfectDrawEnabled: true,
                shadowForStrokeEnabled: false,
                strokeScaleEnabled: false,
            };
        }

        // Default empty config if not first or last window
        return {
            visible: false,
        };
    }

    // Guide image display logic
    private get showLeftGuideImage(): boolean {
        // Always show left guide for first window, otherwise check the leftGuideValue

        if (this.isFirstWindow) {
            return true;
        }

        if (this.areBothGuidesPartition) {
            return false;
        }

        if (this.areBothGuidesSingle) {
            return true;
        }

        if (this.isFirstGuideSingle && this.windowPosition === 2) {
            return true;
        }

        if (this.isSecondGuideSingle && this.windowPosition === 3) {
            return true;
        }

        return false;
    }

    private get showRightGuideImage(): boolean {
        // Always show right guide for last window, otherwise check the rightGuideValue
        if (this.isLastWindow) {
            return true;
        }

        if (this.areBothGuidesPartition) {
            return false;
        }

        if (this.areBothGuidesSingle) {
            return true;
        }

        if (this.isFirstGuideSingle && this.windowPosition === 1) {
            return true;
        }

        if (this.isSecondGuideSingle && this.windowPosition === 2) {
            return true;
        }

        return false;
    }

    private get showMiddleGuideImage(): boolean {
        // Only show middle guide when using Pregradno type and inner guides are needed
        return this.shouldShowPartitionGuide;
    }

    // Left guide image configuration
    private get leftGuideImageConfig() {
        const frameWidth = this.transformedDimensions?.width * this.groupScaleX;
        const scaleFactor = 1.5; // Increase size by 50%
        const offset = 30;

        // Create an Image object for Konva
        const imageObj = new Image();
        imageObj.src = require(`@/assets/guides/left-guide.svg`);

        // Set natural width and height with scale factor
        // This will preserve the original image proportions
        return {
            x: 0 - offset,
            y: -45, // Position vertically
            scaleX: scaleFactor,
            scaleY: scaleFactor,
            image: imageObj,
            perfectDrawEnabled: true,
            shadowForStrokeEnabled: false,
            strokeScaleEnabled: false,
            // Add event listeners directly in the Konva config
            // onMouseenter: () => {
            //     this.hoveredGuide = 'left';
            //     document.body.style.cursor = 'pointer'; // Set cursor directly on body
            // },
            // onMouseleave: () => {
            //     this.hoveredGuide = null;
            //     document.body.style.cursor = 'default'; // Reset cursor on leave
            // },
        };
    }

    // Right guide image configuration
    private get rightGuideImageConfig() {
        const frameWidth = this.transformedDimensions?.width * this.groupScaleX;
        const scaleFactor = 1.5; // Increase size by 50%
        const offset = 30;

        // Create an Image object for Konva
        const imageObj = new Image();
        imageObj.src = require(`@/assets/guides/right-guide.svg`);

        return {
            x: frameWidth + offset, // Position at the right edge
            y: -45, // Position vertically
            scaleX: scaleFactor,
            scaleY: scaleFactor,
            offsetX: 80, // Offset to align right edge
            image: imageObj,
            perfectDrawEnabled: true,
            shadowForStrokeEnabled: false,
            strokeScaleEnabled: false,
            // Add event listeners directly in the Konva config
            // onMouseenter: () => {
            //     this.hoveredGuide = 'right';
            //     document.body.style.cursor = 'pointer'; // Set cursor directly on body
            // },
            // onMouseleave: () => {
            //     this.hoveredGuide = null;
            //     document.body.style.cursor = 'default'; // Reset cursor on leave
            // },
        };
    }

    // Middle guide image configuration (shown when inner guides are needed)
    private get middleGuideImageConfig() {
        const frameWidth = this.transformedDimensions?.width * this.groupScaleX;
        const scaleFactor = 1.5; // Increase size by 50%
        // const offset = 10;

        // Create an Image object for Konva
        const imageObj = new Image();
        imageObj.src = require(`@/assets/guides/middle-guide.svg`);

        return {
            x: frameWidth, // Center horizontally
            y: -45, // Position vertically
            scaleX: scaleFactor,
            scaleY: scaleFactor,
            offsetX: 80 / 2, // Center the image
            image: imageObj,
            perfectDrawEnabled: true,
            shadowForStrokeEnabled: false,
            strokeScaleEnabled: false,
            // Add event listeners directly in the Konva config
            // onMouseenter: () => {
            //     this.hoveredGuide = 'middle';
            //     document.body.style.cursor = 'pointer'; // Set cursor directly on body
            // },
            // onMouseleave: () => {
            //     this.hoveredGuide = null;
            //     document.body.style.cursor = 'default'; // Reset cursor on leave
            // },
        };
    }

    // Configuration for the circles with letters
    private get circlesConfig() {
        const frameWidth = this.transformedDimensions?.width * this.groupScaleX;
        const circleRadius = 22;
        const spacing = 40; // Spacing between circles
        const startX = 30; // Starting position from left

        // Default letters if none provided
        const defaultCircles = [
            { letter: 'L', visible: true },
            { letter: 'D', visible: false },
        ];

        // Use provided circleLetters or default circles
        const circles = this.circleLetters && this.circleLetters.length > 0 ? this.circleLetters : defaultCircles;

        return circles.map((item, index) => {
            // Position circles based on letter type and frame width
            let x;
            switch (item.letter) {
                case 'L':
                    // Left position
                    x = startX;
                    // If first window and using extra width, adjust position
                    if (this.isFirstWindow && this.isUsingExtraWidth) {
                        x = startX; // Move left by extra width
                    }
                    break;
                case 'D':
                    // Right position
                    x = frameWidth - startX;
                    // If last window and using extra width, adjust position
                    if (this.isLastWindow && this.isUsingExtraWidth) {
                        x = frameWidth - startX; // Move right by extra width
                    }
                    break;
                default:
                    // Default spacing for other letters
                    x = startX + index * spacing;
                    // Adjust position based on extra width if applicable
                    if (this.isFirstWindow && this.isUsingExtraWidth) {
                        x = x; // Move left by extra width
                    }
                    break;
            }

            return {
                visible: item.visible,
                config: {
                    x: x,
                    y: 33, // Center of the rectangle
                    radius: circleRadius,
                    fill: '#1a53ff', // Blue circle
                    stroke: '#0033cc',
                    strokeWidth: 1,
                    perfectDrawEnabled: true, // Improves text rendering
                    shadowForStrokeEnabled: false, // Better text performance
                    hitStrokeWidth: 0, // For better performance
                    strokeScaleEnabled: false, // For smoother rendering of circle strokes
                },
                textConfig: {
                    x: x - 5, // Adjust for text centering
                    y: 25, // Adjust for vertical centering
                    text: item.letter,
                    fontSize: 20,
                    fontFamily: 'Arial',
                    fill: 'white',
                    align: 'center',
                    perfectDrawEnabled: true, // Improves text rendering
                    shadowForStrokeEnabled: false, // Better text performance
                    lineJoin: 'round', // Smooths text edges
                    fontStyle: 'bold', // Make text bold for better visibility
                },
            };
        });
    }

    // Vertical yellow line config (rendered when windowConfig.position === '1')
    private get verticalLineConfig() {
        const frameWidth = this.transformedDimensions?.width * this.groupScaleX;
        const frameHeight = this.transformedDimensions?.height * this.groupScaleY;
        const lineWidth = 65; // width of the vertical line on screen
        const offset = 30; // gap after the frame

        // Use the shared color configuration
        const { lineColor, strokeColor } = this.colorConfig;

        return {
            x: frameWidth - offset,
            y: this.headerHeight,
            width: lineWidth,
            height: frameHeight - this.headerHeight,
            fill: lineColor, // Gold/yellow
            stroke: strokeColor,
            strokeWidth: 1,
            opacity: 1,
        };
    }

    // Public method to allow the parent component to move this window group to top
    public moveWindowToTop() {
        // Only render if we have a divider line which means we are using single vodila
        if (this.$refs.windowGroup && this.$refs.dividerLine) {
            const windowGroup = (this.$refs.windowGroup as any)._konvaNode;
            if (windowGroup) {
                windowGroup.moveToTop();
                return true;
            }
        }

        return false;
    }

    // Zoomed left guide image configuration
    // private get zoomedLeftGuideImageConfig() {
    //     const frameWidth = this.transformedDimensions?.width * this.groupScaleX;
    //     const offset = 30;

    //     // Create an Image object for Konva
    //     const imageObj = new Image();
    //     imageObj.src = require(`@/assets/guides/left-guide.svg`);

    //     return {
    //         x: 0 - offset - this.zoomOffset * 2, // Move further left from the original
    //         y: -135 - this.zoomOffset * 1.5, // Move up a bit
    //         scaleX: this.zoomFactor, // Larger scale for zoom effect
    //         scaleY: this.zoomFactor,
    //         image: imageObj,
    //         perfectDrawEnabled: true,
    //         shadowForStrokeEnabled: true,
    //         shadowColor: 'rgba(0,0,0,0.5)',
    //         shadowBlur: 10,
    //         shadowOffset: { x: 2, y: 2 },
    //         shadowOpacity: 0.5,
    //         strokeScaleEnabled: false,
    //         opacity: 1, // Fully opaque
    //         listening: false, // Make this non-interactive so it doesn't interfere with hover events
    //     };
    // }

    // // Zoomed right guide image configuration
    // private get zoomedRightGuideImageConfig() {
    //     const frameWidth = this.transformedDimensions?.width * this.groupScaleX;
    //     const offset = 30;

    //     // Create an Image object for Konva
    //     const imageObj = new Image();
    //     imageObj.src = require(`@/assets/guides/right-guide.svg`);

    //     return {
    //         x: frameWidth + offset + this.zoomOffset * 2, // Move further right
    //         y: -135 - this.zoomOffset * 1.5, // Move up a bit
    //         scaleX: this.zoomFactor,
    //         scaleY: this.zoomFactor,
    //         offsetX: 80, // Same offset as the original image
    //         image: imageObj,
    //         perfectDrawEnabled: true,
    //         shadowForStrokeEnabled: true,
    //         shadowColor: 'rgba(0,0,0,0.5)',
    //         shadowBlur: 10,
    //         shadowOffset: { x: -2, y: 2 },
    //         shadowOpacity: 0.5,
    //         strokeScaleEnabled: false,
    //         opacity: 1, // Fully opaque
    //         listening: false, // Make this non-interactive so it doesn't interfere with hover events
    //     };
    // }

    // Zoomed middle guide image configuration
    // private get zoomedMiddleGuideImageConfig() {
    //     const frameWidth = this.transformedDimensions?.width * this.groupScaleX;

    //     // Create an Image object for Konva
    //     const imageObj = new Image();
    //     imageObj.src = require(`@/assets/guides/middle-guide.svg`);

    //     return {
    //         x: frameWidth, // Keep same horizontal position
    //         y: -45 - this.zoomOffset * 3, // Move up more to avoid overlap
    //         scaleX: this.zoomFactor,
    //         scaleY: this.zoomFactor,
    //         offsetX: 80 / 2, // Center the image
    //         image: imageObj,
    //         perfectDrawEnabled: true,
    //         shadowForStrokeEnabled: true,
    //         shadowColor: 'rgba(0,0,0,0.5)',
    //         shadowBlur: 10,
    //         shadowOffset: { x: 0, y: 2 },
    //         shadowOpacity: 0.5,
    //         strokeScaleEnabled: false,
    //         opacity: 1, // Fully opaque
    //         listening: false, // Make this non-interactive so it doesn't interfere with hover events
    //     };
    // }
}
